import {
  FeatureType,
  RecentPageType,
  type StandardPageRecentItem,
} from '__generated__';
import { useCallback, useEffect } from 'react';

import { useKanso } from '@sbu/contexts.kanso';
import { DynamicFeatureFlag, useFeatureFlag } from '@sbu/hooks.featureflag';
import { useRecents } from '@sbu/hooks.recents';

export type UseCSIEMRecentsConfig = Pick<
  StandardPageRecentItem,
  'accessId' | 'name' | 'standardPageType'
> | null;

export const useCSIEMRecents = (config: UseCSIEMRecentsConfig) => {
  const { addStandardPageRecentItems } = useRecents({
    // We want to avoid calling the initial query for three reasons:
    // 1. We don't read the recents in this hook
    // 2. We want to avoid conflicts with the initial query when triggering a refetch after a mutation
    // 3. Avoid side-effects when Kanso is disabled
    skipInitialRecentsQuery: true,
  });
  const CSIEMRecentsEnabled = useFeatureFlag(
    DynamicFeatureFlag.KansoRecentsGACSIEM
  );
  const { navigationEnabled: kansoNavigationEnabled } = useKanso();

  const { accessId, name, standardPageType } = config ?? {};

  const addRecentItem = useCallback(() => {
    if (
      kansoNavigationEnabled &&
      CSIEMRecentsEnabled &&
      accessId &&
      name &&
      standardPageType
    ) {
      addStandardPageRecentItems([
        {
          __typename: 'StandardPageRecentItem',
          accessId,
          accessTime: Date.now(),
          id: Math.floor(Math.random() * 100000),
          name,
          pageType: RecentPageType.StandardPage,
          standardPageType,
          feature: FeatureType.Csiem,
        },
      ]);
    }
  }, [
    kansoNavigationEnabled,
    CSIEMRecentsEnabled,
    accessId,
    name,
    standardPageType,
    addStandardPageRecentItems,
  ]);

  const addRecentOnDocumentVisible = useCallback(() => {
    if (document.visibilityState === 'visible') {
      addRecentItem();
    }
  }, [addRecentItem]);

  useEffect(() => {
    addRecentItem();
  }, [addRecentItem]);

  useEffect(() => {
    if (kansoNavigationEnabled && CSIEMRecentsEnabled) {
      document.addEventListener('visibilitychange', addRecentOnDocumentVisible);

      return () => {
        document.removeEventListener(
          'visibilitychange',
          addRecentOnDocumentVisible
        );
      };
    }
  }, [kansoNavigationEnabled, CSIEMRecentsEnabled, addRecentOnDocumentVisible]);
};
